import { alpha, Menu, MenuItem, MenuProps, styled } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankOutlinedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined';
import CSS from 'csstype';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  ButtonV2,
  Col,
  Cols,
  Div,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

// IMPORTANT: THIS <SimpleMultiSelect> COMPONENT WILL CAUSE A CONSOLE WARNING DUE TO MUI ATTEMPTING TO SEND A REF
// TODO: REVISIT THIS AFTER UPGRADING MUI

const getBackgroundColor = (isHovered: boolean, isOpen: boolean) => {
  switch (true) {
    case isOpen:
      return publicaColors.lightGray;
    case isHovered:
      return publicaColors.veryLightGray;
    default:
      return 'inherit';
  }
};

const checkboxStyle: CSS.Properties = {
  marginRight: spacing.small,
  color: publicaColors.veryDarkGray,
};

const sortListByLabel = (list: IMenuList) => {
  const sortedList = Object.values(list).sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
  );
  const sortedKeys: string[] = [];
  sortedList.forEach((item) => {
    sortedKeys.push(item.ID);
  });
  return sortedKeys;
};

interface ISimpleMultiSelectItemProps {
  checked: boolean;
  handleCheck: (id: string) => void;
  id: string;
  label: string;
  tKey?: TKey;
}

const SimpleMultiSelectItem = ({
  checked = false,
  handleCheck,
  id,
  label,
  tKey,
}: ISimpleMultiSelectItemProps) => {
  return (
    <MenuItem onClick={() => handleCheck(id)}>
      <Cols>
        <Col paddingTop='4px'>
          {checked ? (
            <CheckBoxIcon style={checkboxStyle} />
          ) : (
            <CheckBoxOutlineBlankOutlinedIcon style={checkboxStyle} />
          )}
        </Col>
        <Col>
          <Text small inlineBlock style={{ verticalAlign: 'text-top' }} tKey={tKey}>
            {label}
          </Text>
        </Col>
      </Cols>
    </MenuItem>
  );
};

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: 4,
    minWidth: 180,
    color: 'inherit',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0px',
    },
    '& .MuiMenuItem-root': {
      paddingTop: '3px',
      paddingBottom: '2px',
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const extractSelectedIDs = (list: IMenuList) => {
  return Object.keys(list).filter((id) => list[id].selected);
};

// TODO: once needed, we can add a custom top/bottom row prop or optional preset buttons

interface ISimpleMultiSelectProps {
  buttonStyle?: CSS.Properties;
  resetKeys?: string[]; // these are the keys that will activate on reset
  disableLabelOverride?: boolean; // will only show label as button label w/o num selected
  disableSort?: boolean; // will prevent alphabetical sort on options
  handleOnAccept?: (selected: string[]) => void;
  handleOnChangeOption?: (selected: string[], id?: string) => void;
  iconPath?: string;
  iconPositionStyle?: CSS.Properties; // use only padding and margin props here
  iconStyle?: CSS.Properties; // these props will go directly into the img element. ex: height: '12px'
  label: string;
  list: IMenuList;
  preselectedKeys: string[];
  singularLabel: string;
  tKey?: TKey;
  singularTKey?: TKey;
}

export const SimpleMultiSelect = ({
  buttonStyle = {},
  resetKeys = [],
  disableLabelOverride,
  disableSort,
  handleOnAccept,
  handleOnChangeOption,
  iconPath,
  iconPositionStyle = {},
  iconStyle = {},
  label,
  list,
  preselectedKeys,
  singularLabel,
}: ISimpleMultiSelectProps): React.JSX.Element => {
  const { t } = usePublicaTranslation();
  const defaultKeys = preselectedKeys || extractSelectedIDs(list);

  const isMounted = useRef(false);
  const [selected, setSelected] = useState<string[]>(defaultKeys);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const keys = disableSort ? Object.keys(list) : sortListByLabel(list);

  // this allows an override if a new list is passed in
  useEffect(() => {
    if (isMounted.current) {
      const updatedSelectedKeys: string[] = [];
      Object.values(list).forEach((item) => {
        if (item.selected) {
          updatedSelectedKeys.push(item.ID);
        }
      });
      setSelected(updatedSelectedKeys);
    }
    isMounted.current = true;
  }, [list]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    unstable_batchedUpdates(() => {
      setAnchorEl(event.currentTarget);
      setIsOpen(!isOpen);
    });
  };

  const handleClose = () => {
    unstable_batchedUpdates(() => {
      setAnchorEl(null);
      setIsOpen(false);
      setIsHovered(false);
    });
    if (handleOnAccept) {
      handleOnAccept(selected);
    }
  };

  const handleCheck = (id: string) => {
    const clonedSelected = cloneDeep(selected);
    const index = clonedSelected.indexOf(id);
    if (index > -1) {
      clonedSelected.splice(index, 1);
      setSelected(clonedSelected);
    } else {
      clonedSelected.push(id);
      setSelected(clonedSelected);
    }
    if (handleOnChangeOption) {
      handleOnChangeOption(clonedSelected, id);
    }
  };

  const handleReset = () => {
    setSelected(resetKeys);
    if (handleOnAccept) {
      handleOnAccept(resetKeys);
    }
  };

  const numSelected = selected.length;
  const updatedLabel = `${numSelected} ${numSelected !== 1 ? label : singularLabel}`;

  return (
    <Div onHover={() => setIsHovered(true)} onLeave={() => setIsHovered(false)}>
      {/* BUTTON */}
      <ButtonV2
        onClick={handleClick}
        clearBackground
        style={{
          padding: '6px 4px 6px 12px',
          backgroundColor: getBackgroundColor(isHovered, isOpen),
          ...buttonStyle,
        }}
      >
        <Cols style={{ alignItems: 'center' }}>
          {iconPath && (
            <Col
              paddingRight='6px'
              displayFlex // this is to center the image vertically
              style={iconPositionStyle}
            >
              <img src={iconPath} height='12px' style={iconStyle} />
            </Col>
          )}
          <Col>
            <Text small bold disableSelect style={{ whiteSpace: 'nowrap' }}>
              {disableLabelOverride ? label : updatedLabel}
            </Text>
          </Col>
          <Col
            displayFlex // this is to center the image vertically
          >
            <ArrowDropDownIcon style={{ color: publicaColors.veryDarkGray }} />
          </Col>
        </Cols>
      </ButtonV2>
      {/* MODAL */}
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Rows style={{ minWidth: '240px' }}>
          <Row
            horizontalPadding
            smallVerticalPadding
            style={{
              borderBottom: `1px solid ${publicaColors.lightGray}`,
              marginBottom: spacing.extraSmall,
            }}
          >
            <Cols
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col flex paddingRight={spacing.medium}>
                <Text extraSmall inlineBlock tKey='NumSelected' tProps={{ count: numSelected }} />
              </Col>
              <Col>
                <TextButton size='extraSmall' onClick={handleReset} allCaps>
                  {t('Reset')}
                </TextButton>
              </Col>
            </Cols>
          </Row>
          {keys.map((key) => {
            const { ID, title, tKey } = list[key];
            return (
              <Row noPadding>
                <SimpleMultiSelectItem
                  checked={selected.some((el) => el === ID)}
                  id={ID}
                  label={title}
                  handleCheck={handleCheck}
                  tKey={tKey}
                />
              </Row>
            );
          })}
          <Row
            horizontalPadding
            smallVerticalPadding
            style={{
              borderTop: `1px solid ${publicaColors.lightGray}`,
              marginTop: spacing.extraSmall,
            }}
          >
            <Cols
              style={{
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Col>
                <TextButton size='extraSmall' onClick={handleClose} allCaps>
                  {t('Close')}
                </TextButton>
              </Col>
            </Cols>
          </Row>
        </Rows>
      </StyledMenu>
    </Div>
  );
};
